import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Mail,
  Phone,
  Briefcase,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Badge, Card } from "@/components/ui";
import { usersApi } from "@/services/api";
import { useAuthStore } from "@/stores/authStore";

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  admin: "Administrador",
  manager: "Gerente",
  seller: "Vendedor",
  client: "Cliente",
};

const ROLE_COLORS: Record<string, "success" | "warning" | "danger" | "info"> = {
  super_admin: "danger",
  admin: "danger",
  manager: "warning",
  seller: "info",
  client: "success",
};

export default function UserViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ["user", id],
    queryFn: () => usersApi.get(id!),
    enabled: !!id,
  });

  const user = (data as any)?.data;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 text-lg">Usuário não encontrado</p>
        <button
          onClick={() => navigate("/admin/users")}
          className="text-accent-primary hover:text-accent-secondary mt-4"
        >
          Voltar para usuários
        </button>
      </div>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Nunca";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/admin/users")}
          className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-text-primary">
            Detalhes do Usuário
          </h1>
          <p className="text-text-secondary mt-1">Informações do usuário</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informações Principais */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <div className="flex items-start gap-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center text-white font-semibold text-4xl flex-shrink-0">
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-text-primary">
                  {user.name}
                </h2>
                <p className="text-text-muted mt-1">{user.email}</p>
                <div className="flex items-center gap-2 mt-4">
                  <Badge variant={(ROLE_COLORS[user.role] || "success") as any}>
                    {ROLE_LABELS[user.role] || user.role}
                  </Badge>
                  {Boolean(user.is_active) ? (
                    <div className="flex items-center gap-1 px-3 py-1 bg-green-500/10 rounded-full">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400">Ativo</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 px-3 py-1 bg-red-500/10 rounded-full">
                      <XCircle className="w-4 h-4 text-red-400" />
                      <span className="text-sm text-red-400">Inativo</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Contato */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              Informações de Contato
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-accent-primary flex-shrink-0" />
                <div>
                  <p className="text-sm text-text-muted">Email</p>
                  <p className="text-text-primary">{user.email}</p>
                </div>
              </div>
              {user.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-accent-primary flex-shrink-0" />
                  <div>
                    <p className="text-sm text-text-muted">Telefone</p>
                    <p className="text-text-primary">{user.phone}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Função */}
          {user.client_name && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Cliente Vinculado
              </h3>
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-accent-primary flex-shrink-0" />
                <div>
                  <p className="text-sm text-text-muted">Cliente</p>
                  <p className="text-text-primary">{user.client_name}</p>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar com Informações Adicionais */}
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              Histórico
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wide">
                  Cadastrado em
                </p>
                <p className="text-text-primary text-sm mt-1">
                  {formatDate(user.created_at)}
                </p>
              </div>
              <div className="h-px bg-white/10" />
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wide">
                  Último acesso
                </p>
                <p className="text-text-primary text-sm mt-1">
                  {formatDate(user.last_login_at)}
                </p>
              </div>
              <div className="h-px bg-white/10" />
              <div>
                <p className="text-xs text-text-muted uppercase tracking-wide">
                  Atualizado em
                </p>
                <p className="text-text-primary text-sm mt-1">
                  {formatDate(user.updated_at)}
                </p>
              </div>
            </div>
          </Card>

          {currentUser?.id !== user.id && (
            <button
              onClick={() => navigate(`/admin/users/${user.id}/edit`)}
              className="w-full px-4 py-2 bg-accent-primary hover:bg-accent-secondary text-white font-semibold rounded-lg transition-colors"
            >
              Editar Usuário
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
